import { createFileRoute, Link } from "@tanstack/react-router";
import { Wordmark } from "@/components/landing/Wordmark";
import { Heart, Stethoscope, ShieldCheck, ArrowRight } from "lucide-react";
import { brandedTitle, safeDesc } from "@/lib/seo-meta";

const META_TITLE = brandedTitle("Sign In");
const META_DESC = safeDesc(
  "Sign in to your Heallexa account as a patient, provider, or admin to manage your profile, listings, and appointments."
);

export const Route = createFileRoute("/login/")({
  head: () => ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESC },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESC },
      { property: "og:url", content: "https://heallexa.com/login" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://heallexa.com/login" }],
  }),
  component: LoginSelector,
});

function LoginSelector() {
  const opts = [
    { to: "/login/patient", icon: Heart, label: "Login as Patient", desc: "Find providers, save favorites, book visits.", color: "text-rose-500" },
    { to: "/login/provider", icon: Stethoscope, label: "Login as Provider", desc: "Claim and manage your practice listing.", color: "text-primary" },
    { to: "/login/admin", icon: ShieldCheck, label: "Login as Admin", desc: "Restricted access for platform admins.", color: "text-amber-600" },
  ] as const;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-3 mb-6">
          <Wordmark className="text-2xl" />
          <h1 className="text-xl font-semibold mt-2">Sign in to your account</h1>
          <p className="text-sm text-muted-foreground text-center">Choose how you want to sign in.</p>
        </div>

        <div className="space-y-2">
          {opts.map(({ to, icon: Icon, label, desc, color }) => (
            <Link
              key={to}
              to={to}
              className="group flex items-center gap-4 border border-border bg-card hover:border-primary/40 hover:bg-accent/40 transition p-4"
            >
              <div className={`h-10 w-10 rounded-full bg-muted flex items-center justify-center ${color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold">{label}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition" />
            </Link>
          ))}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}
